import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { InlineSelect } from "@/components/ui/inline-select"
import { Label } from "@/components/ui/label"
import { availableLocales, locale, t } from "@/lib/i18n"
import { patchInline } from "@/lib/inline-update"

// Language names are shown as endonyms (each language in its own spelling),
// so they intentionally do not go through the translation layer.
const LANGUAGE_NAMES: Record<string, string> = {
  es: "Español",
  en: "English",
}

/**
 * A full document reload, not an Inertia visit. `configureI18n` runs once in
 * the entrypoint's `setup`, and plenty of modules resolve `t()` into top-level
 * constants (`USER_NAV_TABS`, `PROFILE_NAV_ITEMS`) that are frozen at import
 * time. Re-rendering would translate some of the screen and leave the rest in
 * the old language, which is worse than not changing at all.
 *
 * It costs the success toast — the flash is consumed by the visit this reload
 * replaces. Watching the whole interface switch languages says more than a
 * toast would anyway.
 */
function reloadIntoNewLocale() {
  window.location.reload()
}

export function LocaleSelector() {
  const options = availableLocales().map((code) => ({
    value: code,
    label: LANGUAGE_NAMES[code] ?? code,
    testId: `locale-option-${code}`,
  }))

  return (
    <Card variant="borderless">
      <CardHeader>
        <CardTitle>{t("ui.react.components.features.profiles.locale_selector.label")}</CardTitle>
        <CardDescription>{t("ui.react.components.features.profiles.locale_selector.description")}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <Label htmlFor="locale">{t("ui.react.components.features.profiles.locale_selector.label")}</Label>
          <InlineSelect
            id="locale"
            className="w-full"
            data-testid="locale-select-trigger"
            value={locale()}
            options={options}
            onSubmit={(value) => patchInline("/profile/settings", { locale: value }).then(reloadIntoNewLocale)}
          />
        </div>
      </CardContent>
    </Card>
  )
}
